import React, { useEffect, useRef, useState } from 'react'
import { fetchPublic } from '../ticketFetch'

const CATEGORY_OPTIONS = [
  { value: 'finance_related', label: 'Finance Related' },
  { value: 'mdm',             label: 'MDM'             },
  { value: 'others',          label: 'Others'          },
]

const REFUND_MODE_OPTIONS    = ['Customer wallet', 'Subscription energy', 'Dealer wallet']
const STATUS_OPTIONS         = ['Approved', 'Not approved', 'Pending']
const VERIFIED_FROM_OPTIONS  = ['PayU', 'SM billing', 'Accounts team indofast']
const ISSUE_TYPE_OPTIONS     = [
  'Recharge', 'Refund', 'Subscription',
  'Extra amount deducted', 'Minus wallet balance', 'Cashback',
]

const TICKET_STATUS_OPTIONS = [
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed',  label: 'Closed'  },
]

const emptyForm = (resolvedData) => {
  const rf = resolvedData?.resolution_fields || {}
  return {
    category:      rf.category      || '',
    // Finance related
    refund_mode:   rf.refund_mode   || '',
    status:        rf.status        || '',
    verified_from: rf.verified_from || '',
    issue_type:    rf.issue_type    || '',
    // MDM
    description:   rf.description   || '',
    // Common bottom
    ticket_status:  resolvedData?.ticket_status  || '',
    issue_resolved: resolvedData?.issue_resolved || '',
    remarks:        resolvedData?.remarks        || '',
  }
}

export default function CustomerSupportCloseForm({ ticketId, resolvedData, readOnly, onSubmit, submitting }) {
  const [form, setForm]               = useState(() => emptyForm(resolvedData))
  const [errors, setErrors]           = useState({})
  const [rcaFiles, setRcaFiles]       = useState([])       // { file, preview }[]
  const [existingRca, setExistingRca] = useState([])       // { url, name }[]
  const [rcaLoading, setRcaLoading]   = useState(false)
  const [rcaUploading, setRcaUploading] = useState(false)
  const pdfInputRef                   = useRef(null)

  // Fetch existing RCA attachments when MDM category is active
  useEffect(() => {
    if (!ticketId) return
    setRcaLoading(true)
    fetchPublic(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=rca`)
      .then((data) => {
        const list = Array.isArray(data) ? data : []
        setExistingRca(list.map((a) => ({ url: a.file, name: a.file_name?.split('/').pop() || 'rca' })))
      })
      .catch(() => {})
      .finally(() => setRcaLoading(false))
  }, [ticketId])

  async function uploadRcaFiles(files) {
    if (!files.length || !ticketId) return
    setRcaUploading(true)
    try {
      const fd = new FormData()
      files.forEach(({ file }) => fd.append('files', file))
      await fetchPublic(
        `/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=rca`,
        { method: 'POST', body: fd }
      )
      setExistingRca((prev) => [...prev, ...files.map(({ file }) => ({ url: '', name: file.name }))])
      setRcaFiles([])
      if (pdfInputRef.current) pdfInputRef.current.value = ''
    } catch {
      setErrors((prev) => ({ ...prev, rca: 'Upload failed. Please retry.' }))
      throw new Error('upload_failed')
    } finally {
      setRcaUploading(false)
    }
  }

  const hasCategory = !!form.category
  const isClosed   = form.ticket_status === 'closed'
  const isFinance  = form.category === 'finance_related'
  const isMdm      = form.category === 'mdm'
  const isOthers   = form.category === 'others'

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'ticket_status' && value !== 'closed') next.issue_resolved = ''
      if (field === 'category') {
        // clear category-specific fields on switch
        next.refund_mode = ''; next.status = ''; next.verified_from = ''
        next.issue_type = ''; next.description = ''
      }
      return next
    })
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  function validate() {
    const e = {}
    if (!form.category)       e.category      = 'Required'
    if (isFinance) {
      if (!form.refund_mode)  e.refund_mode   = 'Required'
      if (!form.status)       e.status        = 'Required'
      if (!form.verified_from) e.verified_from = 'Required'
      if (!form.issue_type)   e.issue_type    = 'Required'
    }
    if (isMdm && !rcaFiles.length && !existingRca.length && !readOnly) e.rca = 'Please attach at least one RCA file'
    if ((isMdm || isOthers) && !form.description.trim()) e.description = 'Required'
    if (!form.ticket_status)                    e.ticket_status   = 'Required'
    if (isClosed && !form.issue_resolved)       e.issue_resolved  = 'Required'
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    // Upload pending RCA files on submit
    if (isMdm && rcaFiles.length) {
      try { await uploadRcaFiles(rcaFiles) } catch { return }
    }

    const resolution_fields = { category: form.category }
    if (isFinance) {
      resolution_fields.refund_mode   = form.refund_mode
      resolution_fields.status        = form.status
      resolution_fields.verified_from = form.verified_from
      resolution_fields.issue_type    = form.issue_type
    }
    if (isMdm || isOthers) {
      resolution_fields.description = form.description
    }

    onSubmit({
      resolution_fields,
      ticket_status:    form.ticket_status,
      remarks:          form.remarks,
      ...(isMdm && { attachment_type: 'rca' }),
      ...(isClosed && { issue_resolved: form.issue_resolved }),
    })
  }

  return (
    <form className="ctf-section" onSubmit={handleSubmit} noValidate>

      {readOnly && (
        <div className="ctf-readonly-banner">
          &#128274; This ticket is closed — view only
        </div>
      )}

      {/* Category */}
      <Field label="Category" required={!readOnly} error={errors.category}>
        <select
          className="ctf-select"
          value={form.category}
          onChange={(e) => set('category', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select category</option>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* ── Finance Related fields ── */}
      {isFinance && (
        <>
          <Field label="Refund Mode" required={!readOnly} error={errors.refund_mode}>
            <select
              className="ctf-select"
              value={form.refund_mode}
              onChange={(e) => set('refund_mode', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select mode</option>
              {REFUND_MODE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          <Field label="Status" required={!readOnly} error={errors.status}>
            <select
              className="ctf-select"
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select status</option>
              {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          <Field label="Verified From" required={!readOnly} error={errors.verified_from}>
            <select
              className="ctf-select"
              value={form.verified_from}
              onChange={(e) => set('verified_from', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select source</option>
              {VERIFIED_FROM_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          <Field label="Issue Type" required={!readOnly} error={errors.issue_type}>
            <select
              className="ctf-select"
              value={form.issue_type}
              onChange={(e) => set('issue_type', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select issue type</option>
              {ISSUE_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        </>
      )}

      {/* ── MDM fields ── */}
      {isMdm && (
        <>
          <Field label="RCA" required={!readOnly} error={errors.rca}>
            {rcaLoading ? (
              <p className="ctf-hint">Loading…</p>
            ) : (
              <div className="ctf-image-preview-wrap">
                {/* Already uploaded files */}
                {existingRca.map((f, i) => (
                  <div key={i} className="ctf-pdf-preview">
                    <span className="ctf-pdf-name">&#128196; {f.name}</span>
                    {f.url && (
                      <a href={f.url} target="_blank" rel="noreferrer" className="ctf-pdf-link">View</a>
                    )}
                  </div>
                ))}

                {/* Newly selected, not yet uploaded */}
                {rcaFiles.map((entry, i) => (
                  <div key={i} className="ctf-pdf-preview">
                    <span className="ctf-pdf-name">&#128196; {entry.file.name}</span>
                    {!readOnly && (
                      <button
                        type="button"
                        className="ctf-image-remove"
                        onClick={() => setRcaFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}

                {/* Manual upload button */}
                {rcaFiles.length > 0 && !readOnly && (
                  <button
                    type="button"
                    className="ctf-upload-btn"
                    onClick={() => uploadRcaFiles(rcaFiles)}
                    disabled={rcaUploading}
                  >
                    {rcaUploading ? 'Uploading…' : `&#8593; Upload ${rcaFiles.length} file${rcaFiles.length > 1 ? 's' : ''}`}
                  </button>
                )}

                {/* File picker */}
                {!readOnly && (
                  <label className="ctf-file-label" style={existingRca.length || rcaFiles.length ? { marginTop: 4 } : {}}>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf,image/*"
                      multiple
                      className="ctf-file-input"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || [])
                        if (!files.length) return
                        setRcaFiles((prev) => [...prev, ...files.map((file) => ({ file, preview: null }))])
                        setErrors((prev) => ({ ...prev, rca: '' }))
                        if (pdfInputRef.current) pdfInputRef.current.value = ''
                      }}
                    />
                    <span className="ctf-file-btn">
                      {existingRca.length || rcaFiles.length ? '&#128196; Add more files' : '&#128196; Attach files'}
                    </span>
                  </label>
                )}

                {!existingRca.length && !rcaFiles.length && readOnly && (
                  <p className="ctf-hint">No files attached</p>
                )}
              </div>
            )}
          </Field>

          <Field label="Description" required={!readOnly} error={errors.description}>
            <textarea
              className="ctf-textarea"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Add description…"
              readOnly={readOnly}
            />
          </Field>

        </>
      )}

      {/* ── Others fields ── */}
      {isOthers && (
        <Field label="Description" required={!readOnly} error={errors.description}>
          <textarea
            className="ctf-textarea"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Add description…"
            readOnly={readOnly}
          />
        </Field>
      )}

      {/* ── Common bottom fields (always shown) ── */}
      <div className="ctf-divider" />

      <Field label="Ticket Status" required={!readOnly} error={errors.ticket_status}>
        <select
          className="ctf-select"
          value={form.ticket_status}
          onChange={(e) => set('ticket_status', e.target.value)}
          disabled={readOnly || !hasCategory}
        >
          <option value="">Select status</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      <Field
        label="Issue Resolved"
        required={isClosed && !readOnly}
        error={errors.issue_resolved}
        hint={!isClosed && !readOnly ? 'Available only when Ticket Status is Closed' : undefined}
      >
        <div className={`ctf-radio-group ${(!isClosed || readOnly || !hasCategory) ? 'ctf-radio-group--disabled' : ''}`}>
          {['yes', 'no'].map((v) => (
            <label key={v} className="ctf-radio-label">
              <input
                type="radio"
                name="issue_resolved"
                value={v}
                checked={form.issue_resolved === v}
                disabled={!isClosed || readOnly || !hasCategory}
                onChange={() => set('issue_resolved', v)}
                className="ctf-radio"
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Remarks" error={errors.remarks}>
        <textarea
          className="ctf-textarea"
          value={form.remarks}
          onChange={(e) => set('remarks', e.target.value)}
          placeholder="Add any remarks…"
          readOnly={readOnly}
        />
      </Field>

      {!readOnly && (
        <button type="submit" className="ctf-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Update Ticket'}
        </button>
      )}
    </form>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="ctf-field">
      <span className="ctf-label">
        {label}
        {required && <span className="ctf-required">*</span>}
      </span>
      {children}
      {hint  && !error && <span className="ctf-hint">{hint}</span>}
      {error && <span className="ctf-field-error">{error}</span>}
    </div>
  )
}
